import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

interface PrivateRouteProps {
  children: React.ReactElement;
  requireAdmin?: boolean;
  requirePermission?: string;
  requireAnyPermission?: string[];
  requireAllPermissions?: string[];
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  requireAdmin = false,
  requirePermission,
  requireAnyPermission,
  requireAllPermissions
}) => {
  const { user, loading } = useAuth();
  const { hasPermission, hasAnyPermission, hasAllPermissions, isAdmin } = usePermissions();
  const location = useLocation();

  // Mostra loading mentre verifica l'autenticazione
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Se non è autenticato, reindirizza al login
  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // Verifica permessi RBAC
  let hasAccess = true;
  let denialReason = '';

  // 1. Controlla se richiede admin (legacy o RBAC)
  if (requireAdmin) {
    hasAccess = isAdmin;
    if (!hasAccess) {
      denialReason = 'Questa pagina richiede privilegi di amministratore.';
    }
  }

  // 2. Controlla permesso singolo
  if (hasAccess && requirePermission) {
    hasAccess = hasPermission(requirePermission);
    if (!hasAccess) {
      denialReason = `Permesso richiesto: ${requirePermission}`;
    }
  }

  // 3. Controlla almeno uno dei permessi
  if (hasAccess && requireAnyPermission && requireAnyPermission.length > 0) {
    hasAccess = hasAnyPermission(requireAnyPermission);
    if (!hasAccess) {
      denialReason = `Richiesto almeno uno di questi permessi: ${requireAnyPermission.join(', ')}`;
    }
  }

  // 4. Controlla tutti i permessi
  if (hasAccess && requireAllPermissions && requireAllPermissions.length > 0) {
    hasAccess = hasAllPermissions(requireAllPermissions);
    if (!hasAccess) {
      denialReason = `Richiesti tutti questi permessi: ${requireAllPermissions.join(', ')}`;
    }
  }

  // Se non ha accesso, mostra errore
  if (!hasAccess) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-md">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="mt-4 text-xl font-bold text-gray-900 dark:text-white">
              Accesso Negato
            </h2>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              Non hai i permessi necessari per accedere a questa pagina.
            </p>
            {denialReason && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {denialReason}
              </p>
            )}
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Il tuo ruolo attuale è: {user.roleId?.displayName || 'N/A'}
            </p>
            <button
              onClick={() => window.history.back()}
              className="mt-6 bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Torna Indietro
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default PrivateRoute;
